"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { BlogPost, BlogCategory } from "@/types/blog";
import BlogCard from "@/components/blog/BlogCard";

interface BlogListingClientProps {
  posts: BlogPost[];
  categories: BlogCategory[];
  currentCategory: string;
  currentSort: string;
  currentPage: number;
  totalPages: number;
  totalPosts: number;
}

export default function BlogListingClient({
  posts,
  categories,
  currentCategory,
  currentSort,
  currentPage,
  totalPages,
  totalPosts,
}: BlogListingClientProps) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const sortOptions = [
    { value: "latest", label: "Latest Posts" },
    { value: "oldest", label: "Oldest Posts" },
    { value: "popular", label: "Most Popular" },
    { value: "title", label: "Title A-Z" },
  ];

  const updateURL = (
    newCategory?: string,
    newSort?: string,
    newPage?: number
  ) => {
    const params = new URLSearchParams(searchParams);

    if (newCategory !== undefined) {
      if (newCategory === "all") {
        params.delete("category");
      } else {
        params.set("category", newCategory);
      }
    }

    if (newSort !== undefined) {
      if (newSort === "latest") {
        params.delete("sort");
      } else {
        params.set("sort", newSort);
      }
    }

    if (newPage !== undefined) {
      if (newPage === 1) {
        params.delete("page");
      } else {
        params.set("page", newPage.toString());
      }
    }

    const queryString = params.toString();
    const newURL = queryString ? `/blog?${queryString}` : "/blog";
    router.push(newURL);
  };

  const handleCategoryChange = (category: string) => {
    updateURL(category, currentSort, 1);
  };

  const handleSortChange = (sortBy: string) => {
    updateURL(currentCategory, sortBy, 1);
  };

  const handlePageChange = (page: number) => {
    updateURL(currentCategory, currentSort, page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="bg-white py-8">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-20 2xl:px-8">
        {/* Blog Posts Heading */}
        <h2 className="text-3xl font-bold text-gray-900 mb-4 font-[family-name:var(--font-playfair)]">
          Latest Articles
        </h2>

        {/* Horizontal Line */}
        <div className="border-b border-gray-200 mb-8"></div>

        {/* Pill-style Category Filters */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Filter by Category
          </h3>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => handleCategoryChange("all")}
              className={`px-6 py-3 rounded-xl text-sm font-medium transition-colors ${
                currentCategory === "all"
                  ? "bg-[#7771B8] text-white shadow-md"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              All Categories
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => handleCategoryChange(category.slug)}
                className={`px-6 py-3 rounded-xl text-sm font-medium transition-colors ${
                  currentCategory === category.slug
                    ? "bg-[#7771B8] text-white shadow-md"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        {/* Sort and Results */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div className="text-gray-600">
            Showing {posts.length} of {totalPosts} articles
            {currentCategory !== "all" && (
              <span className="ml-2 text-sm">
                in &quot;
                {categories.find((c) => c.slug === currentCategory)?.name ||
                  currentCategory}
                &quot;
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <span className="text-gray-700">Sort by:</span>
            <select
              value={currentSort}
              onChange={(e) => handleSortChange(e.target.value)}
              className="bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 font-medium hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7771B8] focus:border-transparent cursor-pointer"
            >
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Blog Grid */}
        {posts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {posts.map((post) => (
              <BlogCard key={post.id} post={post} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No articles found.</p>
            <p className="text-gray-400 text-sm mt-2">
              Try adjusting your filters or check back later.
            </p>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 hover:border-gray-400 transition-colors"
            >
              Previous
            </button>

            {[...Array(totalPages)].map((_, index) => {
              const page = index + 1;
              return (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`px-4 py-2 border rounded-lg font-medium transition-colors ${
                    currentPage === page
                      ? "bg-[#7771B8] text-white border-[#7771B8]"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100 hover:border-gray-400"
                  }`}
                >
                  {page}
                </button>
              );
            })}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 hover:border-gray-400 transition-colors"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
